import { makeStyles } from "@material-ui/core";
import { grey } from "@material-ui/core/colors";
import { VIMEO_VIDEO_EMBED_STYLES } from "view/components/RichText/styles";

const toolbarHeight = 50;
export const editorRootHeight = 200;
export const resizeButtonSize = 20;

export default makeStyles((theme) => ({
  container: {
    marginTop: 0,
  },
  root: {
    border: `1px solid ${theme.palette.separator.main}`,
    borderRadius: 4,
    position: "relative",
    height: editorRootHeight,
    paddingTop: toolbarHeight,
    "& .ql-toolbar": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: toolbarHeight,
    },
  },
  resizeButton: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: resizeButtonSize,
    height: resizeButtonSize,
  },
  resizeLineBig: {
    position: "absolute",
    width: "100%",
    height: 1,
    background: theme.palette.common.black,
    transform: "rotate(135deg)",
    top: "50%",
  },
  resizeLineSmall: {
    position: "absolute",
    bottom: "20%",
    right: 0,
    width: "50%",
    height: 1,
    background: theme.palette.common.black,
    transform: "rotate(135deg)",
  },
  editTooltip: {
    position: "absolute",
    left: 0,
    top: 0,
    width: 84,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "0.3s opacity",
    borderRadius: 4,
    fontSize: 12,
    color: theme.palette.common.white,
    background: grey[700],
    zIndex: 1000,
    boxShadow:
      "0px 0px 2px 0px rgba(0, 0, 0, 0.50), 0px 4px 8px 0px rgba(0, 0, 0, 0.15)",
    "&.show": {
      opacity: "100% !important",
    },
  },
  integrationButton: {
    position: "relative",
  },
  integrationLogo: {
    position: "absolute",
    width: "10px !important",
    height: "10px !important",
    bottom: 0,
    right: "2px",
  },
  toolbar: {
    borderColor: `${theme.palette.separator.main} !important`,
    "& .ql-picker.ql-expanded": {
      "& .ql-picker-options": {
        boxShadow: "none",
        borderColor: `${theme.palette.separator.main} !important`,
        backgroundColor: theme.palette.otherElementsBackground.main,
        borderRadius: theme.spacing(0.5),
      },
    },
    "& .ql-expanded,.ql-active": {
      "& svg": {
        "& > g": {
          fill: `${theme.palette.primary.main} !important`,
        },
        "& > polyline": {
          stroke: `${theme.palette.primary.main} !important`,
        },
        "& > path,line": {
          stroke: `${theme.palette.primary.main} !important`,
        },
      },
    },
    "& svg": {
      "&:hover": {
        "& > g": {
          fill: `${theme.palette.primary.main} !important`,
        },
        "& > polyline": {
          stroke: `${theme.palette.primary.main} !important`,
        },
        "& > path,line": {
          stroke: `${theme.palette.primary.main} !important`,
        },
      },
      "& > g": {
        fill: `${theme.palette.tableRowIcon.main} !important`,
      },
      "& > polyline": {
        stroke: `${theme.palette.tableRowIcon.main} !important`,
      },
      "& > path,line": {
        stroke: `${theme.palette.tableRowIcon.main} !important`,
      },
      "& > rect": {
        stroke: `${theme.palette.tableRowIcon.main} !important`,
      },
    },
  },
  iframeToolbarButton: {
    display: "inline-flex !important",
    alignItems: "center",
    justifyContent: "center",
    verticalAlign: "middle",
    padding: `${theme.spacing(0.375, 0.625)} !important`,
    "& svg": {
      display: "block",
    },
    "& svg > path": {
      fill: `${theme.palette.tableRowIcon.main} !important`,
      stroke: "none !important",
    },
    "&:hover svg > path, &.ql-active svg > path": {
      fill: `${theme.palette.primary.main} !important`,
    },
  },
  editor: {
    height: "100%",
    overflow: "auto",
    "& .rich-text-image": {
      cursor: "pointer",
      transition: "all .3s",
    },

    "& .rich-text-image:hover": {
      border: `1px solid ${theme.palette.separator.main}`,
      opacity: 0.6,
    },

    "& .ql-toolbar": {
      border: "none",
      borderBottom: `1px solid ${theme.palette.separator.main}`,
      borderRadius: theme.spacing(0.5, 0.5, 0, 0),
      "& > button": {
        padding: theme.spacing(1),
      },
      "& .ql-picker.ql-expanded": {
        "& .ql-picker-label": {
          borderColor: theme.palette.separator.main,
          borderBottom: "none",
        },
        "& .ql-picker-options": {
          boxShadow: "none",
          borderColor: theme.palette.separator.main,
          borderRadius: theme.spacing(0.5),
        },
      },
    },
    "& .ql-container": {
      height: "100%",
      margin: 0,
      border: "none",
      fontSize: "16px",
    },
    "& .ql-editor.ql-blank:before": {
      fontStyle: "normal",
      color: theme.palette.placeholder.main,
    },
    "& .ql-editor": {
      whiteSpace: "normal !important",
      padding: theme.spacing(2),
      ...VIMEO_VIDEO_EMBED_STYLES,
    },
    "& .ql-tooltip": {
      left: "14px !important",
      top: "5px !important",
      height: 45,
      transform: "none",
      border: `1px solid ${theme.palette.separator.main}`,
      borderRadius: theme.spacing(1),
      boxShadow: "none",
      color: `${theme.palette.common.black}`,
      padding: theme.spacing(1, 1.2),
      "& a": {
        color: theme.palette.primary.main,
        fontWeight: 500,
      },
      "& .ql-preview": {
        textDecoration: "underline",
      },
      "& .ql-action::after": {
        borderRight: "none !important",
        borderLeft: `1px solid ${theme.palette.gray1.main}`,
        paddingLeft: theme.spacing(2),
      },
    },
    qlPickerOptions: {
      "& .ql-picker-item": {
        display: "none",
      },
    },
  },
  variableDropdownContainer: {
    position: "relative",
    display: "inline-block",
    "& > button": {
      padding: theme.spacing(1),
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.separator.main}`,
      borderRadius: theme.spacing(0.5),
      cursor: "pointer",
      "&:hover": {
        backgroundColor: theme.palette.gray1.main,
      },
    },
  },
  insertedVariable: {
    color: "blue",
  },
  variableDropdownMenu: {
    display: "flex",
    flexDirection: "column",
    height: "auto",
    position: "absolute",
    top: "100%",
    padding: "1em",
    left: 0,
    border: `1px solid ${theme.palette.separator.main}`,
    backgroundColor: "#ffffff",
    borderRadius: theme.spacing(0.5),
    zIndex: 1000,
    fontSize: "14px",
    "& > span": {
      color: grey[600],
      margin: "5px 0",
    },
    "& > button, & > div > button": {
      "& > span": {
        color: grey[600],
        margin: "5px 0",
      },
      fontSize: "14px",
      display: "block !important",
      margin: "5px 8px",
      padding: theme.spacing(1),
      whiteSpace: "nowrap",
      width: "auto !important",
      textAlign: "left",
      border: "none",
      backgroundColor: theme.palette.background.default,
      cursor: "pointer",
      "&:hover": {
        backgroundColor: theme.palette.gray1.light,
      },
    },
    "& button.highlighted": {
      color: theme.palette.primary.main,
    },
  },
  variablesPanel: {
    position: "absolute",
    top: "100%",
    left: 0,
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    minWidth: 440,
    maxWidth: 560,
    padding: theme.spacing(1.5),
    fontSize: 14,
    backgroundColor: "#ffffff",
    border: `1px solid ${theme.palette.separator.main}`,
    borderRadius: theme.spacing(0.5),
    boxShadow:
      "0px 0px 2px 0px rgba(0, 0, 0, 0.15), 0px 4px 8px 0px rgba(0, 0, 0, 0.15)",
    "& button": {
      display: "block",
      width: "100%",
      textAlign: "left",
      fontSize: 14,
      whiteSpace: "nowrap",
      padding: theme.spacing(0.75, 1),
      border: "none",
      borderRadius: theme.spacing(0.5),
      backgroundColor: theme.palette.background.default,
      cursor: "pointer",
      "&:hover": {
        backgroundColor: theme.palette.gray1.light,
      },
    },
  },
  variablesSearch: {
    margin: theme.spacing(0, -1.5),
    padding: theme.spacing(0, 1.5, 0.5),
    fontSize: 14,
    borderBottom: `1px solid ${theme.palette.separator.main}`,
  },
  variablesSearchIcon: {
    flexShrink: 0,
    width: 16,
    height: 16,
    color: grey[500],
  },
  variablesColumns: {
    display: "flex",
    gap: theme.spacing(3),
    alignItems: "flex-start",
    maxHeight: 500,
    overflowY: "auto",
    overflowX: "hidden",
    // Id-bound two-column panel: long labels wrap inside their column
    // instead of widening it into a horizontal scrollbar.
    // `!important` beats quill.snow.css `.ql-snow .ql-toolbar button`
    // (28x24px, float left), which otherwise squeezes these buttons.
    "& button": {
      width: "100% !important",
      height: "auto !important",
      float: "none !important",
      // Same padding quill.snow.css gave these buttons, keeps 24px rows.
      padding: "3px 5px !important",
      whiteSpace: "normal",
      overflowWrap: "anywhere",
    },
  },
  variablesColumn: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  variableLabel: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  variablesColumnTitle: {
    margin: theme.spacing(0.5, 0, 1),
    color: grey[600],
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
    // A column holding several variable sets separates their headings.
    "&:not(:first-child)": {
      marginTop: theme.spacing(1.5),
    },
  },
  variablesShowGroup: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(1),
  },
  variablesShowName: {
    margin: theme.spacing(0.5, 0),
    color: theme.palette.contrastText.main,
    fontSize: 13,
    fontWeight: 600,
  },
  variablesFooter: {
    paddingTop: theme.spacing(1),
    color: grey[600],
    fontSize: 12,
  },
  infoIcon: {
    fontSize: "16px",
    fill: grey[500],
  },
}));
